import React, { useState } from 'react';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_LENGTH_MESSAGE } from '@/constants/users';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { passwordAPI } from '@/services/api';
import { CosmicBackground } from '@/components/common/login/CosmicBackground';
import { Logo } from '@/components/common/login/Logo';
import { LegalFooter } from '@/components/common/layout/LegalFooter';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // 토큰이 있으면 비밀번호 재설정 모드, 없으면 이메일 입력 모드
  const isResetMode = !!token;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('이메일을 입력해 주세요.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await passwordAPI.requestReset(email);
      setSuccess('비밀번호 재설정 링크가 이메일로 발송되었습니다. 이메일을 확인해 주세요.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || '비밀번호 재설정 요청에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setError('비밀번호를 입력해 주세요.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > PASSWORD_MAX_LENGTH) {
      setError(PASSWORD_LENGTH_MESSAGE);
      return;
    }

    // 대소문자, 숫자, 특수문자 중 최소 2가지 이상 조합
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    const typeCount = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar].filter(Boolean).length;

    if (typeCount < 2) {
      setError('대소문자, 숫자, 특수문자 중 최소 2가지 이상을 조합해 주세요.');
      return;
    }

    if (!token) {
      setError('재설정 링크가 올바르지 않습니다. 비밀번호 재설정을 다시 요청해 주세요.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await passwordAPI.confirmReset(token, newPassword);
      setSuccess('비밀번호를 재설정했습니다. 로그인 화면으로 이동합니다.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || '비밀번호 재설정에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden cosmic-login-bg">
      <CosmicBackground />
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        <Logo />

        <Card className="bg-white shadow-2xl border-0">
          <CardHeader className="pt-6">
            <CardTitle className="text-gray-900 text-xl">
              {isResetMode ? '비밀번호 재설정' : '비밀번호 찾기'}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {isResetMode 
                ? '새 비밀번호를 입력해 주세요'
                : '등록된 이메일 주소를 입력하시면 비밀번호 재설정 링크를 보내드립니다'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pb-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium">오류가 발생했습니다</p>
                    <p className="text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
            {success && (
              <div className="bg-green-50 border-2 border-green-300 text-green-800 px-4 py-3 rounded-md shadow-sm">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium">성공</p>
                    <p className="text-sm mt-1">{success}</p>
                  </div>
                </div>
              </div>
            )}

            {isResetMode ? (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">새 비밀번호</Label>
                  <Input
                    id="new-password"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    placeholder="새 비밀번호를 입력하세요"
                    disabled={loading}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    대소문자, 숫자, 특수문자 중 최소 2가지 이상 조합, {PASSWORD_MIN_LENGTH}자 이상 {PASSWORD_MAX_LENGTH}자 이하
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">비밀번호 확인</Label>
                  <Input
                    id="confirm-password"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    placeholder="비밀번호를 다시 입력하세요"
                    disabled={loading}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                >
                  {loading ? '처리 중...' : '비밀번호 재설정'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">이메일</Label>
                  <Input
                    id="reset-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    placeholder="등록된 이메일 주소를 입력하세요"
                    disabled={loading}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    등록된 이메일 주소로 비밀번호 재설정 링크를 보내드립니다.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                >
                  {loading ? '처리 중...' : '비밀번호 재설정 링크 보내기'}
                </Button>
              </form>
            )}

            <div className="text-center">
              <Button 
                type="button" 
                variant="link"
                className="text-sm"
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                로그인으로 돌아가기
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* 법정 고지는 카드 밖에 넓게 둔다. 좁은 카드 안에서는 링크가 한 글자씩 쪼개졌다 (#24) */}
      <div className="w-full max-w-6xl mt-12 relative z-10">
        <LegalFooter />
      </div>
    </div>
  );
};

export default ResetPasswordPage;
